import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTable, DataTableColumn } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Database, Link2, X, Search, RefreshCw, Building2 } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { pipefyApi } from '@/lib/api-client'
import SubtiposDespesaTab from './SubtiposDespesaTab'
import CatalogoN3GlobalTab from '@/components/pipefy/CatalogoN3GlobalTab'
import AreaEscopoPipefyTab from './AreaEscopoPipefyTab'

interface CentroCusto {
  id: string
  nome: string
  codigo: string | null
  descricao: string | null
  ativo: boolean
  pipefy_database_id: string | null
  pipefy_table_id: string | null
  created_at: string
  updated_at: string
}

interface DatabaseItem {
  id: string
  name: string
  description: string | null
  url: string
  tags?: string[]
}

export default function VincularDatabasesPage() {
  const [centrosCusto, setCentrosCusto] = useState<CentroCusto[]>([])
  const [databases, setDatabases] = useState<DatabaseItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingDatabases, setLoadingDatabases] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [centroSelecionado, setCentroSelecionado] = useState<CentroCusto | null>(null)
  const [centroParaDesvincular, setCentroParaDesvincular] = useState<CentroCusto | null>(null)
  const [databaseSelecionado, setDatabaseSelecionado] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchCentrosCusto()
    fetchDatabases()
  }, [])

  const fetchCentrosCusto = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('solis_centros_custo')
        .select('*')
        .eq('ativo', true)
        .order('nome', { ascending: true })

      if (error) throw error
      setCentrosCusto(data || [])
    } catch (error: any) {
      console.error('Erro ao buscar centros de custo:', error)
      toast.error('Erro ao buscar centros de custo: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchDatabases = async () => {
    setLoadingDatabases(true)
    try {
      const data = await pipefyApi.listDatabases({})
      if (data.success && data.databases) {
        setDatabases(data.databases)
      }
    } catch (error: any) {
      console.error('Erro ao buscar databases:', error)
      toast.error('Erro ao buscar databases do Pipefy')
    } finally {
      setLoadingDatabases(false)
    }
  }

  const handleVincular = async () => {
    if (!centroSelecionado || !databaseSelecionado) {
      toast.error('Selecione um centro de custo e um database')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('solis_centros_custo')
        .update({ pipefy_table_id: databaseSelecionado })
        .eq('id', centroSelecionado.id)

      if (error) throw error

      toast.success('Database vinculado com sucesso!')
      setDialogOpen(false)
      setCentroSelecionado(null)
      setDatabaseSelecionado('')
      fetchCentrosCusto()
    } catch (error: any) {
      console.error('Erro ao vincular database:', error)
      toast.error('Erro ao vincular database: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDesvincularClick = (centro: CentroCusto) => {
    setCentroParaDesvincular(centro)
    setConfirmDialogOpen(true)
  }

  const handleConfirmDesvincular = async () => {
    if (!centroParaDesvincular) return

    setRemoving(true)
    try {
      const { error } = await supabase
        .from('solis_centros_custo')
        .update({ pipefy_table_id: null })
        .eq('id', centroParaDesvincular.id)

      if (error) throw error

      toast.success('Database desvinculado com sucesso!')
      setConfirmDialogOpen(false)
      setCentroParaDesvincular(null)
      fetchCentrosCusto()
    } catch (error: any) {
      console.error('Erro ao desvincular database:', error)
      toast.error('Erro ao desvincular database: ' + error.message)
    } finally {
      setRemoving(false)
    }
  }

  const getDatabaseName = (tableId: string | null | undefined) => {
    if (!tableId) return null
    return databases.find((db) => db.id === tableId)?.name || null
  }

  const getDatabaseInfo = (tableId: string | null | undefined) => {
    if (!tableId) return null
    return databases.find((db) => db.id === tableId) || null
  }

  // Filtrar centros de custo
  const centrosFiltrados = centrosCusto.filter((centro) =>
    centro.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (centro.codigo && centro.codigo.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const columns: DataTableColumn<CentroCusto>[] = [
    {
      key: 'nome',
      label: 'Centro de Custo',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.nome}</span>
          {row.codigo && (
            <span className="text-xs text-muted-foreground">Código: {row.codigo}</span>
          )}
        </div>
      ),
    },
    {
      key: 'descricao',
      label: 'Descrição',
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.descricao || '-'}
        </span>
      ),
    },
    {
      key: 'pipefy_table_id',
      label: 'Database Pipefy',
      render: (row) => {
        const database = getDatabaseInfo(row.pipefy_table_id)
        if (database) {
          return (
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <div className="flex flex-col">
                <span className="font-medium text-sm">{database.name}</span>
                {database.description && (
                  <span className="text-xs text-muted-foreground">{database.description}</span>
                )}
              </div>
            </div>
          )
        }
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Não vinculado
          </Badge>
        )
      },
    },
    {
      key: 'pipefy_database_id',
      label: 'Registro no Database',
      render: (row) => (
        <code className="bg-muted px-2 py-1 rounded text-xs font-mono">
          {row.pipefy_database_id || '-'}
        </code>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      className: 'text-right',
      render: (row) => {
        const database = getDatabaseInfo(row.pipefy_table_id)
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCentroSelecionado(row)
                setDatabaseSelecionado(row.pipefy_table_id || '')
                setDialogOpen(true)
              }}
            >
              <Link2 className="h-3 w-3 mr-1" />
              {database ? 'Alterar' : 'Vincular'}
            </Button>
            {database && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDesvincularClick(row)}
                title="Desvincular database"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
            {database && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(database.url, '_blank')}
                title="Abrir no Pipefy"
              >
                <Database className="h-3 w-3" />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  // Estatísticas
  const totalCentros = centrosCusto.length
  const centrosVinculados = centrosCusto.filter((c) => c.pipefy_table_id).length
  const centrosNaoVinculados = totalCentros - centrosVinculados

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Vincular Databases Pipefy</h2>
          <p className="text-muted-foreground text-sm">
            Gerencie a vinculação de databases do Pipefy aos centros de custo
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDatabases} disabled={loadingDatabases}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingDatabases ? 'animate-spin' : ''}`} />
            Atualizar Databases
          </Button>
          <Button variant="outline" onClick={fetchCentrosCusto} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="catalogo-n3" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1">
          <TabsTrigger value="catalogo-n3">Catálogo N3 (global)</TabsTrigger>
          <TabsTrigger value="area-escopo">Estrutura por área</TabsTrigger>
          <TabsTrigger value="centros-custo">Centros de Custo</TabsTrigger>
          <TabsTrigger value="subtipos">Subtipos (legado)</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo-n3" className="space-y-4">
          <CatalogoN3GlobalTab />
        </TabsContent>

        <TabsContent value="area-escopo" className="space-y-4">
          <AreaEscopoPipefyTab />
        </TabsContent>

        {/* Tab: Centros de Custo */}
        <TabsContent value="centros-custo" className="space-y-4">
          {/* KPIs */}
          <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Centros</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCentros}</div>
            <p className="text-xs text-muted-foreground">Centros de custo ativos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vinculados</CardTitle>
            <Link2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{centrosVinculados}</div>
            <p className="text-xs text-muted-foreground">
              {totalCentros > 0 ? ((centrosVinculados / totalCentros) * 100).toFixed(1) : 0}% vinculados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Não Vinculados</CardTitle>
            <X className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{centrosNaoVinculados}</div>
            <p className="text-xs text-muted-foreground">Aguardando vinculação</p>
          </CardContent>
        </Card>
      </div>

          {/* Tabela */}
          <Card>
            <CardHeader>
              <CardTitle>Centros de Custo</CardTitle>
              <CardDescription>
                Lista de todos os centros de custo e seus databases vinculados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar centro de custo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <DataTable
                  columns={columns}
                  data={centrosFiltrados}
                  loading={loading}
                  emptyText="Nenhum centro de custo encontrado."
                  searchable={false}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Subtipos de Despesa */}
        <TabsContent value="subtipos" className="space-y-4">
          <SubtiposDespesaTab />
        </TabsContent>
      </Tabs>

      {/* Dialog para vincular database */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular Database do Pipefy</DialogTitle>
            <DialogDescription>
              Selecione o database do Pipefy para vincular ao centro de custo "{centroSelecionado?.nome}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Database do Pipefy</label>
              <Select
                value={databaseSelecionado}
                onValueChange={setDatabaseSelecionado}
                disabled={loadingDatabases}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um database..." />
                </SelectTrigger>
                <SelectContent>
                  {databases.map((db) => (
                    <SelectItem key={db.id} value={db.id}>
                      <div className="flex flex-col">
                        <span>{db.name}</span>
                        {db.description && (
                          <span className="text-xs text-muted-foreground">{db.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {centroSelecionado?.pipefy_table_id && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">
                  <strong>Database atual:</strong> {getDatabaseName(centroSelecionado.pipefy_table_id)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleVincular} disabled={saving || !databaseSelecionado}>
              {saving ? 'Salvando...' : 'Vincular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação para desvincular */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Desvinculação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja desvincular o database do centro de custo "{centroParaDesvincular?.nome}"?
              <br />
              <br />
              Esta ação não pode ser desfeita e você precisará vincular novamente manualmente.
            </DialogDescription>
          </DialogHeader>
          {centroParaDesvincular && getDatabaseInfo(centroParaDesvincular.pipefy_table_id) && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm">
                <strong>Database atual:</strong>{' '}
                {getDatabaseInfo(centroParaDesvincular.pipefy_table_id)?.name}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false)
                setCentroParaDesvincular(null)
              }}
              disabled={removing}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDesvincular}
              disabled={removing}
            >
              {removing ? 'Desvinculando...' : 'Sim, Desvincular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

